import React from 'react';

import { useIsInView } from '@lumapps/lumx-hooks/hooks/useIsInView';

import { ITEM_IMPRESSION_THRESHOLD } from '../constants';
import { lumappsAnalyticsPlugin } from '../tracking';
import { TrackingSurface, TargetType, TrackingEventType } from '../types';

/**
 * This hook is tracking impressions.
 * When the tracked item (ref attached) enter the viewport, it will trigger a batched tracking event
 * @param targetId string
 * @param targetType TargetType
 * @param tracking optional tracking metadata (e.g. the surface the item is rendered in)
 * @returns ref to attach to the tracked element
 */
export const useImpressionTracking = (
    targetId: string,
    targetType: TargetType,
    tracking?: { surface?: TrackingSurface },
) => {
    const surface = tracking?.surface;
    /*
     * One impression per target per mount. This is intentionally NOT a re-mount guard: a remount
     * resets the ref and the item re-emits, which is the intended grain. What it prevents is
     * re-emission when the effect re-runs while the item is still in view — e.g. `surface`
     * resolving a render after the impression fired. Keyed by targetId (not a boolean) so a
     * mounted block recycled for a different target still emits that target's impression.
     */
    const lastTrackedTargetId = React.useRef<string>();

    const { ref, inView } = useIsInView({
        threshold: ITEM_IMPRESSION_THRESHOLD,
        triggerOnce: true,
    });

    React.useEffect(() => {
        const alreadyTracked = lastTrackedTargetId.current === targetId;
        if (!inView || alreadyTracked) {
            return;
        }
        lastTrackedTargetId.current = targetId;
        lumappsAnalyticsPlugin.trackAndBatch({
            payload: { action: { targetId, targetType, surface, type: TrackingEventType.VIEW } },
        });
    }, [inView, targetId, targetType, surface]);

    return ref;
};
